// ============================================================================
// FSA DEPRECIATION — SETTINGS MODAL  (ADR-0006)
// ============================================================================
// Per-calc settings: rounding precision (0/1/2 dp), tax rate for the deferred
// tax working, and the book pro-rata convention. Applied to the tab's draft;
// persistence happens with the tab-level Save.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import type { BookProrataConvention } from 'api/types/fsa/depreciationCalc.types';

export interface DepSettings {
  roundingPrecision: number;
  taxRate: number | null;
  bookProrataConvention: BookProrataConvention;
}

interface Props {
  isOpen: boolean;
  settings: DepSettings;
  onSave: (settings: DepSettings) => void;
  onClose: () => void;
}

interface FormData {
  roundingPrecision: string;
  taxRate: string;
  bookProrataConvention: BookProrataConvention;
}

const PRECISION_OPTIONS = [
  { value: '2', label: '2 decimal places' },
  { value: '1', label: '1 decimal place' },
  { value: '0', label: '0 decimal places (whole rupees)' },
];

const CONVENTION_OPTIONS = [
  { value: 'DAY_WISE', label: 'Day-wise from put-to-use date (default)' },
  { value: 'HALF_RATE_180', label: '180-day half rate (IT-style books)' },
];

const FsaDepSettingsModal: React.FC<Props> = ({ isOpen, settings, onSave, onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    roundingPrecision: '2', taxRate: '', bookProrataConvention: 'DAY_WISE',
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const initialSnapshotRef = useRef('');

  useEffect(() => {
    if (!isOpen) return;
    const data: FormData = {
      roundingPrecision: String(settings.roundingPrecision),
      taxRate: settings.taxRate != null ? String(settings.taxRate) : '',
      bookProrataConvention: settings.bookProrataConvention,
    };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    const taxRate = formData.taxRate === '' ? null : parseFloat(formData.taxRate);
    if (taxRate != null && (Number.isNaN(taxRate) || taxRate < 0 || taxRate > 100)) {
      errors.taxRate = 'Tax rate must be between 0 and 100.';
    }
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }
    onSave({
      roundingPrecision: parseInt(formData.roundingPrecision, 10),
      taxRate,
      bookProrataConvention: formData.bookProrataConvention,
    });
    onClose();
  }, [formData, onSave, onClose]);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={handleClose} />
      <div className="modal-container modal-sm" style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <IconSystem.settings size={16} />
            <h2 className="modal-title" style={{ margin: 0 }}>Depreciation Settings</h2>
          </div>
          <button className="modal-close" onClick={handleClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ flex: 1, overflow: 'auto' }}>
          <div className="form-group">
            <SyutSelect
              label="Rounding of Depreciation Values"
              value={formData.roundingPrecision}
              onChange={(v) => setFormData(p => ({ ...p, roundingPrecision: (v as string) || '2' }))}
              options={PRECISION_OPTIONS}
            />
            <div style={hintStyle}>
              Applied to every computed figure in this working paper. ₹ '000-style
              rounding stays with the notes' own rounding pipeline.
            </div>
          </div>

          <div className="form-group" data-field="taxRate">
            <label className="form-label">Tax Rate % (deferred tax)</label>
            <input
              type="number"
              className={`form-input${fieldErrors.taxRate ? ' form-field-error' : ''}`}
              value={formData.taxRate}
              onChange={(e) => {
                setFormData(p => ({ ...p, taxRate: e.target.value }));
                setFieldErrors(p => { const n = { ...p }; delete n.taxRate; return n; });
              }}
              min={0}
              max={100}
              step="0.01"
              placeholder="e.g. 25.17"
              style={{ textAlign: 'right' }}
            />
            {fieldErrors.taxRate && <div className="form-error-message">{fieldErrors.taxRate}</div>}
            <div style={hintStyle}>
              Enacted rate used for the deferred tax on the depreciation timing difference.
            </div>
          </div>

          <div className="form-group">
            <SyutSelect
              label="Book Pro-rata Convention"
              value={formData.bookProrataConvention}
              onChange={(v) => setFormData(p => ({ ...p, bookProrataConvention: (v as BookProrataConvention) || 'DAY_WISE' }))}
              options={CONVENTION_OPTIONS}
            />
            <div style={hintStyle}>
              How book depreciation on additions is prorated. The Income Tax side
              always applies the statutory 180-day rule regardless of this setting.
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave}>Apply Settings</button>
        </div>
      </div>

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="Discard the changed settings?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

const hintStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  color: 'var(--text-secondary)',
  marginTop: 4,
  lineHeight: 1.4,
};

export default FsaDepSettingsModal;
